import { Segment } from './Segment';
import { ITranscriptContainer } from './interfaces';
import { makeAutoObservable } from 'mobx';

/**
 * TranscriptContainer is the top-level container for the entire transcript
 * It contains metadata about the transcript and a list of segments
 */
export class TranscriptContainer implements ITranscriptContainer {
  /** Unique identifier for the transcript */
  id: string;

  /** Story ID of the transcript */
  storyId: string;
  
  /** Language code of the transcript (e.g., 'en-us') */
  language: string;
  
  /** Version of the transcript schema */
  version: number;
  
  /** ISO 8601 date string for creation time */
  createdAt: string;
  
  /** ISO 8601 date string for last modification time */
  modifiedAt: string;
  
  /** List of segments in the transcript */
  segments: Segment[];

  counter: number;
  
  /** Maps segmentId to index in the segments array for quick lookup */
  private segmentMap: Map<string, number>;
  
  /**
   * Creates a new TranscriptContainer
   * 
   * @param id Unique identifier for the transcript
   * @param storyId Story ID of the transcript
   * @param version Version of the transcript schema
   * @param language Language code of the transcript
   * @param segments List of segments in the transcript
   */
  constructor(
    id: string,
    storyId: string,
    version: number,
    language: string,
    segments: Segment[] = [],
  ) {
    // Make all properties observable so MobX-powered UI can react to deep changes.

    this.id = id;
    this.storyId = storyId;
    this.language = language;
    this.version = version;
    this.segments = segments;
    this.counter = 0;
    // Initialize creation and modification times
    const now = new Date().toISOString();
    this.createdAt = now;
    this.modifiedAt = now;
    
    // Build the segment lookup map
    this.segmentMap = new Map();
    this.rebuildSegmentMap();

    // Enable deep reactivity (arrays & nested objects) – crucial for UI updates after commands.
    makeAutoObservable(this);
  }
  
  /**
   * Rebuilds the segment lookup map
   * Call this after any operations that modify the segments array
   */
  rebuildSegmentMap(): void {
    this.segmentMap.clear();
    this.segments.forEach((segment, index) => {
      this.segmentMap.set(segment.id, index);
    });
    this.counter++;
  }
  
  /**
   * Gets a child entity by ID (implements ParentEntity)
   */
  getChild(segmentId: string): Segment | undefined {
    return this.getSegment(segmentId);
  }
  
  /**
   * Adds a child entity (implements ParentEntity)
   */
  addChild(segment: Segment): void {
    this.addSegment(segment);
  }
  
  /**
   * Removes a child entity (implements ParentEntity)
   */
  removeChild(segmentId: string): boolean {
    return this.removeSegment(segmentId);
  }
  
  /**
   * Updates a child entity (implements ParentEntity)
   */
  updateChild(segmentId: string, updatedSegment: Segment): boolean {
    return this.updateSegment(segmentId, updatedSegment);
  }
  
  /**
   * Gets a segment by ID
   * 
   * @param segmentId ID of the segment to get
   * @returns The segment, or undefined if not found
   */
  getSegment(segmentId: string): Segment | undefined {
    const index = this.segmentMap.get(segmentId);
    return index !== undefined ? this.segments[index] : undefined;
  }
  
  /**
   * Adds a segment to the transcript
   * 
   * @param segment Segment to add
   */
  addSegment(segment: Segment): void {
    this.segments.push(segment);
    this.segmentMap.set(segment.id, this.segments.length - 1);
    this.modifiedAt = new Date().toISOString();
    this.counter++;
  }
  
  /**
   * Removes a segment from the transcript
   * 
   * @param segmentId ID of the segment to remove
   * @returns true if the segment was removed, false if not found
   */
  removeSegment(segmentId: string): boolean {
    const index = this.segmentMap.get(segmentId);
    if (index === undefined) {
      return false;
    }
    
    this.segments.splice(index, 1);
    this.rebuildSegmentMap();
    this.modifiedAt = new Date().toISOString();
    this.counter++;
    return true;
  }
  
  /**
   * Updates a segment in the transcript
   * 
   * @param segmentId ID of the segment to update
   * @param updatedSegment Updated segment data
   * @returns true if the segment was updated, false if not found
   */
  updateSegment(segmentId: string, updatedSegment: Segment): boolean {
    const index = this.segmentMap.get(segmentId);
    if (index === undefined) {
      return false;
    }
    
    this.segments[index] = updatedSegment;
    this.modifiedAt = new Date().toISOString();
    this.counter++;
    return true;
  }
  
  /**
   * Gets all segments that overlap with a given time range
   * 
   * @param startTime Start time of the range in seconds
   * @param endTime End time of the range in seconds
   * @returns Array of segments that overlap with the range, even if partially
   */
  getSegmentsByTimeRange(startTime: number, endTime: number): Segment[] {
    return this.segments.filter(segment => 
      (segment.startTime >= startTime && segment.startTime <= endTime) ||
      (segment.endTime >= startTime && segment.endTime <= endTime)
    );
  }

  /**
   * Gets all segments for a specific speaker
   * 
   * @param speakerLabel Label of the speaker to filter by
   * @returns Array of segments for the speaker
   */
  getSegmentsBySpeaker(speakerLabel: string): Segment[] {
    return this.segments.filter(segment => segment.speakerLabel === speakerLabel);
  }
  
  /**
   * Gets the total duration of the transcript in seconds
   */
  get duration(): number {
    if (this.segments.length === 0) {
      return 0;
    }
    
    // Find the latest end time among all segments
    let latestEnd = 0;
    for (const segment of this.segments) {
      if (segment.endTime > latestEnd) {
        latestEnd = segment.endTime;
      }
    }
    
    return latestEnd;
  }
  
  /**
   * Gets the start time of the transcript
   */
  get startTime(): number {
    if (this.segments.length === 0) {
      return 0;
    }
    
    // Find the earliest start time among all segments
    let earliestStart = Number.MAX_VALUE;
    for (const segment of this.segments) {
      if (segment.startTime < earliestStart) {
        earliestStart = segment.startTime;
      }
    }
    
    return earliestStart !== Number.MAX_VALUE ? earliestStart : 0;
  }
  
  /**
   * Gets the end time of the transcript
   */
  get endTime(): number {
    return this.startTime + this.duration;
  }
  
  /**
   * Serializes the transcript container to JSON
   */
  toJSON(): Record<string, any> {
    return {
      id: this.id,
      storyId: this.storyId,
      language: this.language,
      version: this.version,
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
      segments: this.segments.map(segment => segment.toJSON())
    };
  }
  
  /**
   * Creates a TranscriptContainer from a JSON object
   * 
   * @param json JSON object to deserialize
   * @returns A new TranscriptContainer instance
   */
  static fromJSON(json: any): TranscriptContainer {
    const container = new TranscriptContainer(
      json.id,
      json.storyId,
      json.version,
      json.language,
      [],
    );
    
    // Set timestamps from JSON
    container.createdAt = json.createdAt;
    container.modifiedAt = json.modifiedAt;
    
    // Deserialize segments
    container.segments = json.segments.map((segmentJson: any) => 
      Segment.fromJSON(segmentJson)
    );
    
    // Rebuild the segment map
    container.rebuildSegmentMap();
    
    return container;
  }

  incrementCounter(): void {
    this.counter++;
  }
}